import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Info, TriangleAlert, XCircle } from 'lucide-react';
import { _registerToastDispatcher, type Toast, type ToastVariant } from '@/shared/lib/toast';

const ICONS: Record<ToastVariant, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  error: XCircle,
};

const COLORS: Record<ToastVariant, string> = {
  info: 'border-brand-400 bg-brand-50 text-brand',
  success: 'border-green-300 bg-green-50 text-green-800',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
  error: 'border-red-300 bg-red-50 text-red-800',
};

/**
 * Renderiza los toasts activos. Monta una sola vez en el árbol raíz.
 * El dispatcher se registra al mount y se limpia al unmount.
 */
export function Toaster() {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    _registerToastDispatcher((t) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const full: Toast = { id, ...t };
      setToasts((prev) => [...prev, full]);
      const timer = setTimeout(() => dismiss(id), full.durationMs);
      timers.current.set(id, timer);
    });
    return () => {
      _registerToastDispatcher(null);
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
    };
  }, []);

  const dismiss = (id: string) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    setToasts((prev) => prev.filter((x) => x.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[10000] flex flex-col items-center gap-2 px-3"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + var(--nav-inferior) + 25px)' }}
      role="region"
      aria-label={t('common.notifications_region')}
    >
      {toasts.map((toast) => {
        const Icon = ICONS[toast.variant];
        return (
          <div
            key={toast.id}
            role={toast.variant === 'error' || toast.variant === 'warning' ? 'alert' : 'status'}
            className={`pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-lg border px-3 py-2.5 shadow-md ${COLORS[toast.variant]}`}
          >
            <Icon size={18} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{toast.title}</div>
              {toast.body && <div className="mt-0.5 text-xs opacity-90">{toast.body}</div>}
            </div>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="ml-1 shrink-0 rounded p-0.5 text-current opacity-60 transition-opacity hover:opacity-100"
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default Toaster;
